import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogOpenEvent } from '@theme/dialog';

/** Long enough that a fast typist fires one request per word, short enough to feel live. */
const DEBOUNCE_MS = 250;

/**
 * Predictive search matches on prefixes, so one character is already a useful query: "ч" brings
 * back the чашки. Nothing is gained by making the shopper type a second character first.
 */
const DEFAULT_MIN_QUERY_LENGTH = 1;

/**
 * Shortest query worth retrying on a miss, and how many characters may be shaved off the end.
 * Predictive search matches prefixes but not typos, so "чашкаа" finds nothing while "чашка"
 * finds twelve products. Trimming the tail recovers the most common typo — a fumbled ending —
 * without pretending to be real fuzzy matching, which only synonyms in the Search & Discovery
 * app can give us.
 */
const TYPO_RETRY_MIN_LENGTH = 4;
const TYPO_RETRY_MAX_TRIM = 2;

/** Marks the history entry pushed on open, so the mobile back button closes search. */
const HISTORY_STATE_KEY = 'searchOverlay';

/**
 * Predictive search living inside the navbar.
 *
 * Markup: snippets/search-modal.liquid. Results: sections/predictive-search.liquid, fetched
 * through the Section Rendering API rather than /search/suggest.json, so money formatting,
 * locale-prefixed URLs and image srcsets arrive already rendered by Liquid.
 *
 * Opening does not cover the navbar — it flips the navbar into a second state via
 * [data-search-open], which is why there is no <dialog> here. That keeps the real cart button
 * in the row, so the side cart needs no duplicate markup and no z-index arbitration.
 *
 * Three panel states, on [data-state]:
 *   empty      — nothing typed: popular terms + recommended tiles
 *   results    — at least one hit
 *   no-results — nothing matched: the message, recommended products, and the popular terms.
 *                The shopper is never left staring at an apology with nowhere to go.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input.
 * @property {HTMLElement} predictiveSearchResults - Container the results are morphed into.
 * @property {HTMLElement} emptyState - The static empty state.
 * @property {HTMLElement} [panel] - The dropdown hanging below the navbar.
 * @property {HTMLElement} [body] - The scrolling area inside the panel.
 * @property {HTMLElement} [resetButton] - Clears the text. Never closes search.
 * @property {HTMLElement} [backButton] - Closes search.
 * @property {HTMLElement} [footer] - Holds the "view all results" submit button.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'emptyState'];

  #controller = new AbortController();

  /** @type {AbortController | null} */
  #activeFetch = null;

  #open = false;

  #historyPushed = false;

  /** @type {HTMLElement | null} */
  #lastTrigger = null;

  /** @type {number} */
  #previousScrollY = 0;

  get #navbar() {
    return this.closest('.roro-navbar');
  }

  get #minQueryLength() {
    return Number(this.dataset.minQueryLength) || DEFAULT_MIN_QUERY_LENGTH;
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    document.addEventListener('keydown', this.#handleKeydown, { signal });
    document.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    document.addEventListener('touchmove', this.#handleTouchMove, { signal, passive: false });
    document.addEventListener('wheel', this.#handleWheel, { signal, passive: false });
    window.addEventListener('popstate', this.#handlePopState, { signal });

    // Opening the cart closes search. They are two different answers to "what am I doing now",
    // and the drawer's own scroll lock fights the navbar's sticky positioning if both are live.
    for (const drawer of document.querySelectorAll('cart-drawer-component')) {
      drawer.addEventListener(DialogOpenEvent.eventName, this.#handleCartOpen, { signal });
    }

    this.addEventListener('click', this.#handlePanelClick, { signal });

    this.#setState('empty');

    if (document.readyState === 'complete') {
      this.#scheduleTileImages();
    } else {
      window.addEventListener('load', this.#scheduleTileImages, { once: true, signal });
    }
  }

  /**
   * The «Рекомендуємо» tiles sit in a panel hidden with `visibility: hidden` so it can fade, and to
   * the browser that still counts as rendered: their `loading="lazy"` images were fetched on every
   * page (~110 KB), right while the product photo was downloading. search-modal.liquid writes
   * their URLs to data-src / data-srcset instead; they come back once the page has loaded and the
   * browser is idle, or at once if search is opened before that.
   */
  #restoreTileImages = () => {
    const pending = this.querySelectorAll('.roro-search__tile-image[data-srcset], .roro-search__tile-image[data-src]');

    for (const image of pending) {
      if (image.dataset.srcset) image.srcset = image.dataset.srcset;
      if (image.dataset.src) image.src = image.dataset.src;
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-src');
    }
  };

  #scheduleTileImages = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(this.#restoreTileImages, { timeout: 3000 });
    } else {
      setTimeout(this.#restoreTileImages, 1200);
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /* --------------------------------------------------------------------- open / close */

  /**
   * Bound to the navbar's search buttons via `on:click="#RoroSearch/open"`.
   * @param {MouseEvent} [event]
   */
  open = (event) => {
    if (this.#open) return;

    this.#restoreTileImages();

    const target = event?.target;
    this.#lastTrigger = target instanceof HTMLElement ? target.closest('[data-roro-search-trigger]') : null;

    this.#open = true;
    this.#navbar?.setAttribute('data-search-open', '');
    this.#syncTriggers(true);

    // The input was display:none a statement ago. Reading a layout property forces the style
    // and layout flush that makes it focusable, so this focus() still happens inside the click
    // that opened search — the only way iOS Safari raises the on-screen keyboard.
    void this.refs.searchInput.offsetWidth;
    this.refs.searchInput.focus();

    this.#pushHistoryEntry();
  };

  /**
   * Bound to the back arrow; also called on Escape, on a click outside, when the cart opens,
   * and when a result is followed.
   */
  close = () => {
    if (!this.#open) return;

    this.#open = false;
    this.#navbar?.removeAttribute('data-search-open');
    this.#syncTriggers(false);
    this.#clearSearch();

    // Focus goes back where it came from, so a keyboard user is not dumped at the top of the
    // document. The trigger was display:none until the attribute above came off, and a
    // display:none element cannot take focus — so force the style flush first, exactly as
    // open() does in the other direction.
    if (this.#lastTrigger) {
      void this.#lastTrigger.offsetWidth;
      this.#lastTrigger.focus();
      this.#lastTrigger = null;
    }

    if (this.#historyPushed) {
      this.#historyPushed = false;
      history.back();
    }
  };

  /**
   * Prevents background touch scroll on mobile while allowing search panel scrolling.
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    if (!this.#open) return;
    const target = event.target;
    if (target instanceof Node && this.refs.panel?.contains(target)) {
      return;
    }
    event.preventDefault();
  };

  /**
   * Prevents background wheel scroll while search dropdown is open.
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    if (!this.#open) return;
    const target = event.target;
    if (target instanceof Node && !this.refs.panel?.contains(target)) {
      event.preventDefault();
    }
  };

  /**
   * @param {boolean} expanded
   */
  #syncTriggers(expanded) {
    for (const trigger of document.querySelectorAll('[data-roro-search-trigger]')) {
      trigger.setAttribute('aria-expanded', String(expanded));
    }
  }

  #handleCartOpen = () => this.close();

  /**
   * Following a result closes search. On a hard navigation the page tears down anyway, but the
   * theme can also soft-navigate (assets/view-transitions.js), and that would leave the navbar
   * stuck in its search state on the page the shopper just landed on.
   * @param {MouseEvent} event
   */
  #handlePanelClick = (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('a[href]')) this.close();
  };

  /**
   * Escape closes; Cmd/Ctrl+K toggles from anywhere.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.#open ? this.close() : this.open();
      return;
    }

    if (event.key === 'Escape' && this.#open) {
      event.preventDefault();
      this.close();
    }
  };

  /**
   * Pressing anywhere outside the navbar closes search.
   *
   * This is the whole "click outside" mechanism — there is no scrim swallowing the page. A
   * full-screen catcher would make the page behind unclickable and, with the panel over most
   * of the viewport, would read as if the site had frozen. The panel lives inside .roro-navbar,
   * so one `contains` covers the field, the back arrow, the cart and the results alike.
   *
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (!this.#open) return;

    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.#navbar?.contains(target)) return;

    this.close();
  };

  /* -------------------------------------------------------------------------- history */

  #pushHistoryEntry() {
    if (this.#historyPushed) return;

    // Deliberately no URL change. Flagging search with something like `?search=open` would
    // follow the shopper out the moment they click a result: into the shared link, into
    // analytics and into the referrer. A state-only entry gives the back button the same job.
    history.pushState({ [HISTORY_STATE_KEY]: true }, '');
    this.#historyPushed = true;
  }

  /**
   * @param {PopStateEvent} event
   */
  #handlePopState = (event) => {
    if (!this.#open) return;
    if (event.state?.[HISTORY_STATE_KEY]) return;

    // The browser already popped our entry; clearing the flag stops close() from calling
    // history.back() a second time and skipping a page the shopper did want.
    this.#historyPushed = false;
    this.close();
  };

  /* --------------------------------------------------------------------------- states */

  /**
   * @param {'empty' | 'results' | 'no-results'} state
   */
  #setState(state) {
    this.dataset.state = state;
    this.refs.searchInput.setAttribute('aria-expanded', String(state !== 'empty'));
  }

  /* ------------------------------------------------------------------------ searching */

  /** Bound to the input via `on:input`. */
  search = debounce(() => this.#handleQueryChange(), DEBOUNCE_MS);

  /**
   * Bound to the single × in the field. Clears the text and nothing else — it must never close
   * search, which is what the back arrow and a click outside are for.
   */
  resetSearch = () => {
    this.refs.searchInput.value = '';
    this.refs.searchInput.focus();
    this.search.cancel?.();
    this.#handleQueryChange();
  };

  #handleQueryChange() {
    const term = this.refs.searchInput.value.trim();

    this.#currentIndex = -1;
    this.#toggleResetButton(term.length > 0);

    if (term.length < this.#minQueryLength) {
      this.#abortActiveFetch();
      this.#clearResults();
      this.#setState('empty');
      return;
    }

    this.#getSearchResults(term);
  }

  /**
   * Clears the query without touching focus, so reopening search never shows the previous
   * shopper's search.
   */
  #clearSearch() {
    this.search.cancel?.();
    this.#abortActiveFetch();
    this.refs.searchInput.value = '';
    this.#toggleResetButton(false);
    this.#clearResults();
    this.#currentIndex = -1;
    this.#setState('empty');
  }

  /**
   * @param {string} searchTerm
   * @returns {URL}
   */
  #buildResultsUrl(searchTerm) {
    const url = new URL(Theme.routes.predictive_search_url, location.origin);

    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,collection,page,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    // Every field Shopify will match a predictive query against.
    //   tag    — the campaign collections (Make a Wish, Джерело, For Love…) are tag-driven, so
    //            without this "for love" finds nothing.
    //   body   — the descriptions carry the material and technique words ("кераміка",
    //            "ручна робота") that shoppers actually type but that no title contains.
    //            Worth the extra noise on a 60-80 product catalogue; revisit if it ever grows.
    //   sku    — lets staff pull up a product from an order line.
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,variants.sku,vendor,tag,body'
    );

    return url;
  }

  /**
   * @param {string} searchTerm
   * @returns {Promise<HTMLElement | null>}
   */
  async #fetchResults(searchTerm) {
    const { sectionId } = this.dataset;
    if (!sectionId) return null;

    // sectionRenderer keeps its own Map, keyed by the full section URL, and de-duplicates
    // in-flight requests — so deleting characters replays an earlier query from memory instead
    // of hitting the network again.
    const markup = await sectionRenderer.getSectionHTML(sectionId, true, this.#buildResultsUrl(searchTerm));

    return /** @type {HTMLElement | null} */ (
      new DOMParser().parseFromString(markup, 'text/html').getElementById('predictive-search-results')
    );
  }

  /**
   * @param {HTMLElement | null} results
   */
  #isMiss(results) {
    return !results || 'noResults' in results.dataset;
  }

  /**
   * @param {string} searchTerm
   */
  async #getSearchResults(searchTerm) {
    const abortController = this.#createAbortController();
    const { predictiveSearchResults } = this.refs;

    // No spinner: a spinner appearing and disappearing between keystrokes shifts the layout.
    // Fading the container keeps every box exactly where it is.
    predictiveSearchResults.dataset.loading = '';

    try {
      let results = await this.#fetchResults(searchTerm);

      // Without this guard a slow response for an abandoned query can land after a fast one for
      // the query actually in the box, and the shopper sees results for text they deleted.
      if (abortController.signal.aborted) return;

      // Shave the tail one character at a time until something matches. Only ever replaces a
      // miss, so a query that already found products is never second-guessed.
      for (let trimmed = 1; this.#isMiss(results) && trimmed <= TYPO_RETRY_MAX_TRIM; trimmed++) {
        const stem = searchTerm.slice(0, -trimmed);
        if (stem.length < TYPO_RETRY_MIN_LENGTH) break;

        const retry = await this.#fetchResults(stem);
        if (abortController.signal.aborted) return;
        if (!this.#isMiss(retry)) results = retry;
      }

      this.#renderResults(results);
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error(error);
    } finally {
      if (!abortController.signal.aborted) delete predictiveSearchResults.dataset.loading;
    }
  }

  /**
   * @param {HTMLElement | null} incoming
   */
  #renderResults(incoming) {
    const { predictiveSearchResults } = this.refs;

    if (!incoming) {
      this.#clearResults();
      this.#setState('empty');
      return;
    }

    const existing = predictiveSearchResults.firstElementChild;

    if (existing?.id === incoming.id) {
      // Morphing rather than replacing keeps untouched cards (and their loaded images) in place
      // between keystrokes, so the grid does not flash white on every character.
      morph(existing, incoming);
    } else {
      predictiveSearchResults.replaceChildren(incoming);
    }

    this.#currentIndex = -1;
    this.#setState('noResults' in incoming.dataset ? 'no-results' : 'results');
    this.refs.body?.scrollTo({ top: 0, behavior: 'instant' });
  }

  #clearResults() {
    this.refs.predictiveSearchResults.replaceChildren();
    delete this.refs.predictiveSearchResults.dataset.loading;
  }

  /**
   * @param {boolean} visible
   */
  #toggleResetButton(visible) {
    const { resetButton } = this.refs;
    if (resetButton) resetButton.hidden = !visible;
  }

  #createAbortController() {
    this.#abortActiveFetch();
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  #abortActiveFetch() {
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  /* ------------------------------------------------------------- keyboard navigation */

  /**
   * Every navigable result, in visual order. sections/predictive-search.liquid marks them with
   * [data-result-item] and gives each a unique id for aria-activedescendant.
   * @returns {HTMLElement[]}
   */
  get #items() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.refs.predictiveSearchResults.querySelectorAll('[data-result-item]'))
    );
  }

  get #currentIndex() {
    return this.#items.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    const items = this.#items;
    const { searchInput } = this.refs;

    for (const [itemIndex, item] of items.entries()) {
      item.setAttribute('aria-selected', String(itemIndex === index));
    }

    const active = items[index];

    if (active) {
      // Focus never leaves the input — that is what keeps this a combobox and, on touch
      // devices, keeps the keyboard from collapsing mid-search. The highlight reaches assistive
      // tech through aria-activedescendant instead.
      searchInput.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    } else {
      searchInput.removeAttribute('aria-activedescendant');
    }
  }

  /**
   * Bound to the form via `on:keydown`.
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    // Escape is handled once, on the document, so it works wherever focus happens to be.
    // Tab is left to the browser: hijacking it would break the one navigation pattern keyboard
    // users expect, and it is how they reach the cart and the results.
    const items = this.#items;
    if (!items.length) return;

    const currentIndex = this.#currentIndex;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#currentIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        break;

      case 'Enter': {
        const active = items[currentIndex];
        if (!active) return; // Nothing highlighted: let the form submit through to /search.

        event.preventDefault();
        const link = active instanceof HTMLAnchorElement ? active : active.querySelector('a');
        link?.click();
        break;
      }
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
